import { useCallback, useEffect, useRef, useState } from "react";
import {
  LOCK_HEARTBEAT_MS,
  acquireLock,
  addCastMember,
  addComment,
  addDialogueCue,
  addEpisode,
  duplicateEpisode,
  importEpisodeBackup,
  copyEpisodeToDocument,
  duplicateScene,
  copySceneToEpisode,
  moveSceneToEpisode,
  copySceneToDocument,
  formatEpisodeDisplayTitle,
  splitScene,
  mergeScenes,
  addScene,
  addSeason,
  createEmptyProduction,
  createId,
  getCueSide,
  insertCueAfter,
  insertCueBefore,
  isLockHeldBy,
  listEpisodes,
  absorbRemoteCueTombstones,
  documentNeedsSyncRepublish,
  mergeDocumentCueNotes,
  mergeRemoteCuePreservingNotes,
  moveCueBlock,
  reorderEpisodes,
  moveEpisodeToSeason,
  moveScene,
  normalizeDocument,
  hasExpiredVaultItems,
  releaseLock,
  removeCastMember,
  removeComment,
  removeCue,
  removeEpisode,
  removeScene,
  moveSceneToTrash,
  moveEpisodeToTrash,
  permanentlyDeleteScene,
  permanentlyDeleteEpisode,
  emptyTrash,
  emptyArchive,
  archiveScene,
  submitDirectorScene,
  insertSubmittedIntoProgram,
  restoreSceneFromArchive,
  restoreSceneFromTrash,
  restoreEpisodeFromTrash,
  isNormalEpisode,
  renewLock,
  setCuePrintBorderBelow,
  setCueNote,
  setSceneNote,
  setCueCameraMeta,
  setSceneCrewRoles,
  setEpisodeCrewRoles,
  addCrewJobTitle,
  removeCrewJobTitle,
  setCrewPersonOnEpisodePeers,
  deactivateCrewPersonSeason,
  removeCrewPersonFromEpisodePeers,
  setEpisodeProgramTexts,
  setProductionNoteColumns,
  hideNoteColumnInEpisode,
  removeNoteColumnEverywhere,
  setProductionStatusSteps,
  setProductionUseSeasons,
  setSceneStatusSteps,
  setEpisodeStatusSteps,
  toggleSceneStatusStep,
  toggleEpisodeStatusStep,
  setVisualRowSpan,
  setAudioRowSpan,
  setCueCharacter,
  updateCastMember,
  updateComment,
  updateCueSide,
  updateEpisodeMeta,
  updateProductionMeta,
  updateSeasonMeta,
  updateSceneMeta,
  type CastMember,
  type EpisodeView,
  type Id,
  type ItemNumber,
  type PresenceUser,
  type ScriptColumn,
  type ScriptDocument,
  type SceneCrewRole,
  type CrewSeasonDeactivateMode,
  type ProgramText,
  type DirectorScenePayload,
  type StatusStepDef,
} from "@supermanus/document-model";
import {
  ScriptEditor,
  downloadEpisodeBackup,
  loadSettingsForUser,
  pageUrlBase,
  parseShareSearch,
  pickEpisodeBackupFile,
  readEpisodeBackupFile,
  resolveShareLocation,
  writeShareUrl,
  type CellRef,
  type ShareLocation,
} from "@supermanus/editor";
import {
  cacheLicense,
  openApiSession,
  type LicenseStatus,
} from "@supermanus/license-client";
import {
  createHttpStore,
  HttpStore,
  RevisionConflictError,
  isNetworkError,
  type LocalStore,
  type ProjectMeta,
} from "@supermanus/local-store";
import {
  HttpSyncClient,
  type ClientToServerMessage,
  type ServerToClientMessage,
} from "@supermanus/sync-protocol";
import {
  useDocumentHistory,
  type HistoryPersistOptions,
} from "./useDocumentHistory";
import { ProductionHome } from "./ProductionHome";
import { API_BASE } from "./apiBase";

const LICENSE_KEY_STORAGE = "minproduktion.licenseKey";
const LAST_PRODUCTION_KEY = "minproduktion.lastProductionId";
const USER_STORAGE_KEY = "minproduktion.user";
const LEGACY_LICENSE_KEY_STORAGE = "supermanus.licenseKey";
const LEGACY_LAST_PRODUCTION_KEY = "supermanus.lastProductionId";
const LEGACY_USER_STORAGE_KEY = "supermanus.user";
/** Wait this long after a failed poll/save before showing the offline banner. */
const UNREACHABLE_GRACE_MS = 10_000;

function storageGet(key: string, legacy: string): string | null {
  return localStorage.getItem(key) ?? localStorage.getItem(legacy);
}

function loadUser(): { userId: Id; displayName: string } {
  const existing = storageGet(USER_STORAGE_KEY, LEGACY_USER_STORAGE_KEY);
  if (existing) return JSON.parse(existing) as { userId: Id; displayName: string };
  const user = {
    userId: createId(),
    displayName: `Forfatter ${Math.floor(Math.random() * 90 + 10)}`,
  };
  localStorage.setItem(USER_STORAGE_KEY, JSON.stringify(user));
  return user;
}

function saveUser(user: { userId: Id; displayName: string }) {
  localStorage.setItem(USER_STORAGE_KEY, JSON.stringify(user));
}

function mergeRemoteSnapshot(
  local: ScriptDocument,
  remote: ScriptDocument,
  userId: Id,
): ScriptDocument {
  const merged = mergeDocumentCueNotes(local, remote);
  const cues = { ...merged.cues };
  const now = Date.now();
  for (const [id, cue] of Object.entries(local.cues)) {
    const remoteCue = cues[id];
    if (!remoteCue) continue;
    let next = remoteCue;
    for (const column of ["visual", "audio"] as ScriptColumn[]) {
      const side = getCueSide(cue, column);
      if (isLockHeldBy(side, userId, now)) {
        next = { ...next, [column]: side };
      }
    }
    cues[id] = next;
  }
  return { ...merged, cues };
}

function isUnlockedLicense(status: LicenseStatus): boolean {
  return status === "ok" || status === "grace";
}

export function App() {
  const [user, setUser] = useState(loadUser);
  const [store, setStore] = useState<LocalStore | null>(null);
  const [projects, setProjects] = useState<ProjectMeta[]>([]);
  const [homeBusy, setHomeBusy] = useState(false);
  const [doc, setDoc] = useState<ScriptDocument | null>(null);
  const [activeEpisodeId, setActiveEpisodeId] = useState<Id | null>(null);
  const [activeSceneId, setActiveSceneId] = useState<Id | null>(null);
  const [view, setView] = useState<EpisodeView>("timing");
  const [editorInit, setEditorInit] = useState<{
    screen: "dashboard" | "episode";
    noteColumnId: Id | null;
  }>({ screen: "dashboard", noteColumnId: null });
  const [shareError, setShareError] = useState<string | null>(null);
  const bootShareRef = useRef(parseShareSearch());
  const bootShareDoneRef = useRef(false);
  const [presence, setPresence] = useState<PresenceUser[]>([]);
  const [syncStatus, setSyncStatus] = useState<
    "offline" | "connecting" | "online" | "error"
  >("offline");
  const [licenseStatus, setLicenseStatus] = useState<LicenseStatus>("readonly");
  const [licenseReady, setLicenseReady] = useState(false);
  const [licenseMessage, setLicenseMessage] = useState<string | null>(null);
  const [licenseKey, setLicenseKey] = useState(
    () => storageGet(LICENSE_KEY_STORAGE, LEGACY_LICENSE_KEY_STORAGE) ?? "",
  );
  const [needsName, setNeedsName] = useState(false);
  const [claimName, setClaimName] = useState("");
  const syncRef = useRef<HttpSyncClient | null>(null);
  const docRef = useRef(doc);
  // Intentionally NOT syncing docRef from `doc` each render — that undoes
  // optimistic persist writes when a stale React render flushes.
  const [focusCell, setFocusCell] = useState<CellRef | null>(null);
  const [historyVersion, setHistoryVersion] = useState(0);
  const storeRef = useRef(store);
  storeRef.current = store;
  const crewSaveTimerRef = useRef<number>(0);
  const programTextSaveTimerRef = useRef<number>(0);
  const cueSaveTimerRef = useRef<number>(0);
  const noteSaveTimerRef = useRef<number>(0);
  const sceneMetaSaveTimerRef = useRef<number>(0);
  const dirtyRef = useRef(false);
  const syncStatusRef = useRef(syncStatus);
  syncStatusRef.current = syncStatus;
  const recoveringRef = useRef(false);
  const unreachableTimerRef = useRef<number>(0);
  const pendingUnreachableRef = useRef<"offline" | "error" | null>(null);
  const handleSyncMessageRef = useRef<(msg: ServerToClientMessage) => void>(
    () => {},
  );

  const serverReachable = useCallback(() => {
    const status = syncStatusRef.current;
    return status === "online" || status === "connecting";
  }, []);

  const clearUnreachableTimer = useCallback(() => {
    if (unreachableTimerRef.current) {
      window.clearTimeout(unreachableTimerRef.current);
      unreachableTimerRef.current = 0;
    }
    pendingUnreachableRef.current = null;
  }, []);

  const noteUnreachable = useCallback((kind: "offline" | "error" = "offline") => {
    const current = syncStatusRef.current;
    if (current === "offline" || current === "error") {
      if (kind === "error" && current !== "error") setSyncStatus("error");
      return;
    }
    if (pendingUnreachableRef.current === "error" && kind === "offline") return;
    pendingUnreachableRef.current = kind;
    if (unreachableTimerRef.current) return;
    unreachableTimerRef.current = window.setTimeout(() => {
      unreachableTimerRef.current = 0;
      const pending = pendingUnreachableRef.current;
      pendingUnreachableRef.current = null;
      if (pending) setSyncStatus(pending);
    }, UNREACHABLE_GRACE_MS);
  }, []);

  const markOnline = useCallback(() => {
    clearUnreachableTimer();
    if (syncStatusRef.current !== "online") setSyncStatus("online");
  }, [clearUnreachableTimer]);

  const sendSync = useCallback(
    (msg: ClientToServerMessage) => {
      if (!serverReachable()) {
        if (
          msg.type === "cue.upsert" ||
          msg.type === "cue.move" ||
          msg.type === "comment.create"
        ) {
          dirtyRef.current = true;
        }
        return;
      }
      syncRef.current?.send(msg);
    },
    [serverReachable],
  );

  const flushPendingSave = useCallback(async () => {
    const current = docRef.current;
    const s = storeRef.current;
    if (!current || !s || recoveringRef.current) return;
    recoveringRef.current = true;
    try {
      await s.saveProject(current);
      dirtyRef.current = false;
      markOnline();
    } catch (err) {
      if (err instanceof RevisionConflictError && s instanceof HttpStore) {
        s.setRevision(err.document.production.id, err.revision);
        const latest = docRef.current ?? current;
        const merged = mergeDocumentCueNotes(
          latest,
          normalizeDocument(err.document),
        );
        docRef.current = merged;
        setDoc(merged);
        try {
          await s.saveProject(merged);
          dirtyRef.current = false;
          markOnline();
          return;
        } catch (retryErr) {
          dirtyRef.current = true;
          noteUnreachable(isNetworkError(retryErr) ? "offline" : "error");
          return;
        }
      }
      dirtyRef.current = true;
      noteUnreachable(isNetworkError(err) ? "offline" : "error");
    } finally {
      recoveringRef.current = false;
    }
  }, [noteUnreachable, markOnline]);

  const applyHistoricDoc = useCallback(async (next: ScriptDocument) => {
    docRef.current = next;
    setDoc(next);
    setHistoryVersion((v) => v + 1);
    setFocusCell(null);
    const s = storeRef.current;
    if (!s) return;
    if (!serverReachable()) {
      dirtyRef.current = true;
      return;
    }
    try {
      await s.saveProject(next);
      dirtyRef.current = false;
      markOnline();
    } catch (err) {
      dirtyRef.current = true;
      noteUnreachable(isNetworkError(err) ? "offline" : "error");
    }
  }, [noteUnreachable, serverReachable, markOnline]);

  const { pushBeforeChange, clear: clearHistory } = useDocumentHistory(
    docRef,
    applyHistoricDoc,
  );

  const persist = useCallback(
    async (
      updater: ScriptDocument | ((prev: ScriptDocument) => ScriptDocument),
      options: HistoryPersistOptions = {},
    ): Promise<ScriptDocument | null> => {
      const prev = docRef.current;
      if (!prev) return null;
      if (
        options.recordHistory !== false &&
        user?.userId &&
        loadSettingsForUser(user.userId).viewOnly
      ) {
        return prev;
      }
      const next = typeof updater === "function" ? updater(prev) : updater;
      if (next === prev) return prev;
      pushBeforeChange(prev, options);
      docRef.current = next;
      setDoc(next);
      const shouldSaveToStore =
        Boolean(store) &&
        options.recordHistory !== false &&
        !options.coalesceKey;
      if (shouldSaveToStore && store) {
        // Block snapshot merges from overwriting structural edits (trash move,
        // etc.) until save finishes or we explicitly keep dirty.
        dirtyRef.current = true;
        if (!serverReachable()) {
          return next;
        }
        try {
          await Promise.resolve();
          // After the yield, docRef may include newer edits (paste into a new
          // row, etc.). Prefer that document when it is at least as new as
          // `next`; otherwise merge so structural writes (e.g. Season) stay
          // the base without dropping local-only cues or typed content.
          const latest = docRef.current ?? next;
          const toSave =
            latest.production.updatedAt >= next.production.updatedAt
              ? latest
              : mergeDocumentCueNotes(latest, next);
          if (toSave !== docRef.current) {
            docRef.current = toSave;
            setDoc(toSave);
          }
          await store.saveProject(toSave);
          dirtyRef.current = false;
          markOnline();
        } catch (err) {
          if (err instanceof RevisionConflictError) {
            if (store instanceof HttpStore) {
              store.setRevision(err.document.production.id, err.revision);
              const latest = docRef.current ?? next;
              // Merge live local (incl. deletes) onto the server doc so a
              // concurrent cue.upsert cannot resurrect removed rows.
              const merged = mergeDocumentCueNotes(
                latest,
                normalizeDocument(err.document),
              );
              docRef.current = merged;
              setDoc(merged);
              try {
                await store.saveProject(merged);
                dirtyRef.current = false;
                markOnline();
                return merged;
              } catch (retryErr) {
                dirtyRef.current = true;
                noteUnreachable(isNetworkError(retryErr) ? "offline" : "error");
                return merged;
              }
            }
            return next;
          }
          dirtyRef.current = true;
          noteUnreachable(isNetworkError(err) ? "offline" : "error");
          return next;
        }
      }
      return next;
    },
    [pushBeforeChange, store, serverReachable, noteUnreachable, markOnline, user.userId],
  );

  const refreshProjects = useCallback(async (local?: LocalStore | null) => {
    const s = local ?? storeRef.current;
    if (!s) return;
    try {
      setProjects(await s.listProjects());
    } catch {
      setProjects([]);
    }
  }, []);

  const openProduction = useCallback(
    (loaded: ScriptDocument, share?: ShareLocation | null) => {
      const hadExpiredVault = hasExpiredVaultItems(loaded);
      const normalized = normalizeDocument(loaded);
      const resolved = share
        ? resolveShareLocation(normalized, share)
        : resolveShareLocation(normalized, {
            productionId: normalized.production.id,
          });
      docRef.current = normalized;
      setDoc(normalized);
      clearHistory();
      setHistoryVersion((v) => v + 1);
      setActiveEpisodeId(resolved.episodeId);
      setActiveSceneId(resolved.sceneId);
      setView(resolved.view);
      setEditorInit({
        screen: resolved.screen,
        noteColumnId: resolved.noteColumnId,
      });
      setFocusCell(null);
      setShareError(null);
      localStorage.setItem(LAST_PRODUCTION_KEY, normalized.production.id);
      writeShareUrl(
        resolved.screen === "dashboard"
          ? { productionId: normalized.production.id }
          : {
              productionId: normalized.production.id,
              episodeId: resolved.episodeId ?? undefined,
              view: resolved.view,
              sceneId: resolved.sceneId ?? undefined,
              noteColumnId: resolved.noteColumnId ?? undefined,
            },
      );
      if (hadExpiredVault) {
        const s = storeRef.current;
        if (s) {
          void s.saveProject(normalized).catch((err) => {
            console.error(err);
          });
        }
      }
    },
    [clearHistory],
  );

  useEffect(() => {
    let cancelled = false;
    (async () => {
      const local = createHttpStore(API_BASE);
      if (cancelled) return;
      setStore(local);
      storeRef.current = local;

      const stored = (
        storageGet(LICENSE_KEY_STORAGE, LEGACY_LICENSE_KEY_STORAGE) ?? ""
      ).trim();
      if (!stored) {
        try {
          await fetch(`${API_BASE.replace(/\/$/, "")}/session`, {
            method: "DELETE",
            credentials: "include",
          });
        } catch {
          /* ignore leftover session */
        }
        if (cancelled) return;
        setLicenseStatus("readonly");
        setLicenseMessage(null);
        setProjects([]);
        setLicenseReady(true);
        return;
      }

      const license = await openApiSession(API_BASE, {
        licenseKey: stored,
      });
      if (cancelled) return;
      setLicenseStatus(license.status);
      setLicenseMessage(license.message);
      if (license.needsName) {
        setNeedsName(true);
        setLicenseKey(stored);
        setProjects([]);
      } else if (isUnlockedLicense(license.status)) {
        setNeedsName(false);
        if (license.user) {
          setUser(license.user);
          saveUser(license.user);
        }
        await refreshProjects(local);
      } else {
        setNeedsName(false);
        setProjects([]);
      }
      setLicenseReady(true);
    })();
    return () => {
      cancelled = true;
    };
    // License key is checked on startup from localStorage and when the user
    // clicks “Tjek licens” — not on every keystroke in the license field.
  }, [refreshProjects]);

  async function handleOpenProduction(
    id: string,
    share?: ShareLocation | null,
  ) {
    if (!store || !isUnlockedLicense(licenseStatus)) return;
    setHomeBusy(true);
    setShareError(null);
    try {
      const load = () => store.loadProject(id);
      let loaded: ScriptDocument | null;
      try {
        loaded = await load();
      } catch (err) {
        const status = (err as { status?: number }).status;
        if (status === 401 && licenseKey.trim()) {
          const result = await openApiSession(API_BASE, {
            licenseKey: licenseKey.trim(),
          });
          setLicenseStatus(result.status);
          setLicenseMessage(result.message);
          if (result.user) {
            setUser(result.user);
            saveUser(result.user);
          }
          if (!isUnlockedLicense(result.status)) {
            setShareError(
              "Sessionen er udløbet. Klik Tjek licens, og prøv igen.",
            );
            return;
          }
          loaded = await load();
        } else {
          throw err;
        }
      }
      if (!loaded) {
        setShareError(
          "Produktionen blev ikke fundet, eller du har ikke adgang til den.",
        );
        await refreshProjects();
        return;
      }
      openProduction(loaded, share);
    } catch {
      setShareError(
        "Produktionen kunne ikke åbnes. Tjek forbindelsen, og prøv igen.",
      );
    } finally {
      setHomeBusy(false);
    }
  }

  useEffect(() => {
    if (bootShareDoneRef.current) return;
    if (!store || !licenseReady) return;
    if (!isUnlockedLicense(licenseStatus)) return;
    const loc = bootShareRef.current;
    bootShareDoneRef.current = true;
    if (!loc?.productionId) return;
    void handleOpenProduction(loc.productionId, loc);
  }, [licenseReady, licenseStatus, store]);

  async function handleCreateProduction(title: string) {
    if (!store || !isUnlockedLicense(licenseStatus)) return;
    setHomeBusy(true);
    try {
      const created = createEmptyProduction(title);
      await store.saveProject(created);
      await refreshProjects();
      openProduction(created);
    } finally {
      setHomeBusy(false);
    }
  }

  async function handleDeleteProduction(id: string) {
    if (!store || !isUnlockedLicense(licenseStatus)) return;
    setHomeBusy(true);
    try {
      await store.deleteProject(id);
      if (
        storageGet(LAST_PRODUCTION_KEY, LEGACY_LAST_PRODUCTION_KEY) === id
      ) {
        localStorage.removeItem(LAST_PRODUCTION_KEY);
        localStorage.removeItem(LEGACY_LAST_PRODUCTION_KEY);
      }
      await refreshProjects();
    } finally {
      setHomeBusy(false);
    }
  }

  function handleGoHome() {
    docRef.current = null;
    setDoc(null);
    clearHistory();
    setActiveEpisodeId(null);
    setActiveSceneId(null);
    setFocusCell(null);
    setPresence([]);
    clearUnreachableTimer();
    setSyncStatus("offline");
    dirtyRef.current = false;
    writeShareUrl(null);
    if (isUnlockedLicense(licenseStatus)) {
      void refreshProjects();
    } else {
      setProjects([]);
    }
  }

  const onRenameDisplayName = useCallback((name: string) => {
    const trimmed = name.trim() || "Forfatter";
    setUser((prev) => {
      const next = { ...prev, displayName: trimmed };
      saveUser(next);
      return next;
    });
    void (async () => {
      try {
        const res = await fetch(`${API_BASE.replace(/\/$/, "")}/session/name`, {
          method: "POST",
          credentials: "include",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({ displayName: trimmed }),
        });
        if (!res.ok) return;
        const data = (await res.json()) as {
          user?: { userId: string; displayName: string };
        };
        if (data.user?.userId && data.user.displayName) {
          saveUser(data.user);
          setUser(data.user);
        }
      } catch {
        /* keep local name */
      }
    })();
  }, []);

  useEffect(() => {
    if (!doc) return;
    const revisions = storeRef.current instanceof HttpStore ? storeRef.current : undefined;
    const client = new HttpSyncClient(API_BASE, revisions);
    syncRef.current = client;
    setSyncStatus("connecting");
    dirtyRef.current = false;
    const unsub = client.onMessage((msg) => handleSyncMessageRef.current(msg));
    let heartbeat: number | undefined;

    (async () => {
      try {
        await client.connect();
        client.send({
          type: "auth",
          token: licenseKey,
          displayName: user.displayName,
          userId: user.userId,
        });
        client.send({
          type: "join_project",
          projectId: doc.production.id,
          document: doc,
        });

        heartbeat = window.setInterval(() => {
          const current = docRef.current;
          if (!current || !client.connected) return;
          if (
            syncStatusRef.current === "offline" ||
            syncStatusRef.current === "error"
          ) {
            return;
          }
          for (const cue of Object.values(current.cues)) {
            for (const column of ["visual", "audio"] as ScriptColumn[]) {
              const side = getCueSide(cue, column);
              if (side.lockOwnerId === user.userId && side.lockExpiresAt) {
                client.send({
                  type: "lock.heartbeat",
                  projectId: current.production.id,
                  cueId: cue.id,
                  column,
                });
                void persist(
                  (prev) => renewLock(prev, cue.id, column, user.userId),
                  { recordHistory: false },
                );
              }
            }
          }
        }, LOCK_HEARTBEAT_MS);
      } catch {
        noteUnreachable("offline");
      }
    })();

    return () => {
      unsub();
      if (heartbeat) window.clearInterval(heartbeat);
      clearUnreachableTimer();
      client.disconnect();
      syncRef.current = null;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [doc?.production.id, user.displayName, user.userId, licenseKey]);

  useEffect(() => {
    if (!doc) return;
    const probeSync = () => {
      const current = docRef.current;
      if (!current) return;
      const client = syncRef.current;
      if (!client) return;
      // Wake / tab-focus: clear a stuck offline banner and force a fresh poll.
      // A hung pre-sleep fetch used to leave pollInFlight true forever so
      // recovery never marked us online again.
      if (
        syncStatusRef.current === "offline" ||
        syncStatusRef.current === "error"
      ) {
        clearUnreachableTimer();
        setSyncStatus("connecting");
      }
      client.send({
        type: "sync.request",
        projectId: current.production.id,
      });
      if (dirtyRef.current) void flushPendingSave();
    };
    const onOffline = () => {
      // After sleep, browsers sometimes fire `offline` while still reporting
      // online. Trust failed requests instead of a spurious event.
      if (navigator.onLine) return;
      noteUnreachable("offline");
    };
    const onOnline = () => {
      probeSync();
    };
    const onVisible = () => {
      if (document.visibilityState !== "visible") return;
      probeSync();
    };
    const onBeforeUnload = (event: BeforeUnloadEvent) => {
      if (!dirtyRef.current) return;
      event.preventDefault();
      event.returnValue = "";
    };
    window.addEventListener("offline", onOffline);
    window.addEventListener("online", onOnline);
    window.addEventListener("focus", probeSync);
    window.addEventListener("pageshow", probeSync);
    document.addEventListener("visibilitychange", onVisible);
    document.addEventListener("resume", probeSync);
    window.addEventListener("beforeunload", onBeforeUnload);
    let lastBeat = Date.now();
    const beatTimer = window.setInterval(() => {
      const now = Date.now();
      const gap = now - lastBeat;
      lastBeat = now;
      if (gap > 15_000) probeSync();
    }, 2_000);
    return () => {
      window.clearInterval(beatTimer);
      window.removeEventListener("offline", onOffline);
      window.removeEventListener("online", onOnline);
      window.removeEventListener("focus", probeSync);
      window.removeEventListener("pageshow", probeSync);
      document.removeEventListener("visibilitychange", onVisible);
      document.removeEventListener("resume", probeSync);
      window.removeEventListener("beforeunload", onBeforeUnload);
    };
  }, [
    clearUnreachableTimer,
    doc?.production.id,
    flushPendingSave,
    noteUnreachable,
  ]);

  function handleSyncMessage(msg: ServerToClientMessage) {
    if (!docRef.current) return;

    switch (msg.type) {
      case "error": {
        if (msg.code === "network") {
          noteUnreachable("offline");
          return;
        }
        if (msg.code === "unauthenticated") {
          const current = docRef.current;
          const client = syncRef.current;
          if (current && client && "recoverSession" in client) {
            clearUnreachableTimer();
            setSyncStatus("connecting");
            void (client as HttpSyncClient)
              .recoverSession(current.production.id)
              .then((ok) => {
                if (!ok && syncStatusRef.current === "connecting") {
                  setSyncStatus("error");
                }
              });
            return;
          }
          setSyncStatus("error");
        }
        return;
      }
      case "project.snapshot": {
        setPresence(msg.presence);
        markOnline();
        if (dirtyRef.current) {
          void flushPendingSave();
          return;
        }
        const remote = normalizeDocument(msg.document);
        const prev = docRef.current;
        if (!prev) break;
        let next =
          prev.production.updatedAt < remote.production.updatedAt
            ? mergeRemoteSnapshot(prev, remote, user.userId)
            : absorbRemoteCueTombstones(prev, remote);
        if (next !== prev) {
          docRef.current = next;
          setDoc(next);
        }
        // Merge used to stay local-only (recordHistory: false → no save),
        // so a later reload resurrected deletes. Push the corrected doc.
        if (documentNeedsSyncRepublish(next, remote)) {
          dirtyRef.current = true;
          void flushPendingSave();
        }
        break;
      }
      case "presence.update":
        setPresence(msg.users);
        if (
          (syncStatusRef.current === "offline" ||
            syncStatusRef.current === "error") &&
          dirtyRef.current
        ) {
          void flushPendingSave();
        }
        markOnline();
        break;
      case "cue.updated":
        void persist((prev) => {
          const existing = prev.cues[msg.cue.id];
          if (!existing) return prev;
          // Per-column: never let an older echo overwrite a side the user
          // has already typed past.
          const visual =
            existing.visual.revision > msg.cue.visual.revision
              ? existing.visual
              : msg.cue.visual;
          const audio =
            existing.audio.revision > msg.cue.audio.revision
              ? existing.audio
              : msg.cue.audio;
          if (
            visual === existing.visual &&
            audio === existing.audio &&
            existing.updatedAt === msg.cue.updatedAt
          ) {
            return prev;
          }
          return {
            ...prev,
            cues: {
              ...prev.cues,
              [msg.cue.id]: mergeRemoteCuePreservingNotes(existing, {
                ...msg.cue,
                visual,
                audio,
              }),
            },
            production: {
              ...prev.production,
              updatedAt:
                prev.production.updatedAt >= msg.cue.updatedAt
                  ? prev.production.updatedAt
                  : msg.cue.updatedAt,
            },
          };
        }, { recordHistory: false });
        break;
      case "cue.moved":
        void persist((prev) => {
          const scene = prev.scenes[msg.sceneId];
          if (!scene) return prev;
          if (scene.cueIds.length > msg.cueIds.length) return prev;
          return {
            ...prev,
            scenes: {
              ...prev.scenes,
              [msg.sceneId]: {
                ...scene,
                cueIds: msg.cueIds,
              },
            },
          };
        }, { recordHistory: false });
        break;
      case "lock.granted":
        void persist((prev) => {
          const cue = prev.cues[msg.cueId];
          if (!cue) return prev;
          return {
            ...prev,
            cues: {
              ...prev.cues,
              [msg.cueId]: {
                ...cue,
                [msg.column]: {
                  ...getCueSide(cue, msg.column),
                  lockOwnerId: msg.lockOwnerId,
                  lockOwnerName: msg.lockOwnerName,
                  lockExpiresAt: msg.lockExpiresAt,
                },
              },
            },
          };
        }, { recordHistory: false });
        break;
      case "lock.released":
        void persist((prev) => {
          const cue = prev.cues[msg.cueId];
          if (!cue) return prev;
          return {
            ...prev,
            cues: {
              ...prev.cues,
              [msg.cueId]: {
                ...cue,
                [msg.column]: {
                  ...getCueSide(cue, msg.column),
                  lockOwnerId: null,
                  lockOwnerName: null,
                  lockExpiresAt: null,
                },
              },
            },
          };
        }, { recordHistory: false });
        break;
      case "cue.conflict": {
        localStorage.setItem(
          `minproduktion.orphan.${msg.cueId}.${msg.column}.${Date.now()}`,
          msg.orphanContent,
        );
        void persist((prev) => {
          const existing = prev.cues[msg.cueId];
          if (!existing) return prev;
          const serverSide = getCueSide(msg.serverCue, msg.column);
          const localSide = getCueSide(existing, msg.column);
          // Prefer live local text (higher rev) or the orphan the server
          // rejected — never silently discard the user's latest keystrokes.
          const keepContent =
            localSide.revision > serverSide.revision
              ? localSide.content
              : msg.orphanContent || localSide.content;
          const aligned = {
            ...prev,
            cues: {
              ...prev.cues,
              [msg.cueId]: mergeRemoteCuePreservingNotes(
                existing,
                msg.serverCue,
              ),
            },
          };
          if (keepContent === serverSide.content) return aligned;
          const next = updateCueSide(
            aligned,
            msg.cueId,
            msg.column,
            keepContent,
            user.userId,
          );
          sendSync({
            type: "cue.upsert",
            projectId: next.production.id,
            cue: next.cues[msg.cueId],
            column: msg.column,
            baseRevision: serverSide.revision,
          });
          return next;
        }, { recordHistory: false });
        break;
      }
      default:
        break;
    }
  }
  handleSyncMessageRef.current = handleSyncMessage;

  const onCueFocus = useCallback(
    (cueId: Id, column: ScriptColumn) => {
      if (licenseStatus === "readonly") return;
      try {
        void persist((prev) => {
          if (!prev.cues[cueId]) return prev;
          return acquireLock(
            prev,
            cueId,
            column,
            user.userId,
            user.displayName,
          );
        }, { recordHistory: false });
        const current = docRef.current;
        if (!current?.cues[cueId]) return;
        const side = getCueSide(current.cues[cueId], column);
        sendSync({
          type: "lock.acquire",
          projectId: current.production.id,
          cueId,
          column,
          expectedRevision: side.revision,
        });
        sendSync({
          type: "presence",
          projectId: current.production.id,
          episodeId: activeEpisodeId ?? undefined,
          sceneId: current.cues[cueId].sceneId,
          cueId,
          column,
        });
      } catch {
        // locked
      }
    },
    [activeEpisodeId, licenseStatus, persist, sendSync, user.displayName, user.userId],
  );

  const onCueBlur = useCallback(
    (cueId: Id, column: ScriptColumn) => {
      window.clearTimeout(cueSaveTimerRef.current);
      void flushPendingSave();
      void persist((prev) => {
        try {
          if (!prev.cues[cueId]) return prev;
          return releaseLock(prev, cueId, column, user.userId);
        } catch {
          return prev;
        }
      }, { recordHistory: false });
      const projectId = docRef.current?.production.id;
      if (projectId) {
        sendSync({
          type: "lock.release",
          projectId,
          cueId,
          column,
        });
      }
    },
    [flushPendingSave, persist, sendSync, user.userId],
  );

  const onCueChange = useCallback(
    (cueId: Id, column: ScriptColumn, content: string) => {
      if (licenseStatus === "readonly") return;
      void persist(
        (prev) => {
          const cue = prev.cues[cueId];
          if (!cue) return prev;
          const side = getCueSide(cue, column);
          if (side.content === content) return prev;
          const baseRevision = side.revision;
          const next = updateCueSide(prev, cueId, column, content, user.userId);
          sendSync({
            type: "cue.upsert",
            projectId: next.production.id,
            cue: next.cues[cueId],
            column,
            baseRevision,
          });
          return next;
        },
        { coalesceKey: `cue:${cueId}:${column}` },
      );
      dirtyRef.current = true;
      window.clearTimeout(cueSaveTimerRef.current);
      cueSaveTimerRef.current = window.setTimeout(() => {
        void flushPendingSave();
      }, 400);
    },
    [flushPendingSave, licenseStatus, persist, sendSync, user.userId],
  );

  const onMoveCueBlock = useCallback(
    (cueId: Id, beforeCueId: Id | null) => {
      if (!doc || licenseStatus === "readonly" || !activeSceneId) return;
      const next = moveCueBlock(doc, activeSceneId, cueId, beforeCueId);
      if (next === doc) return;
      void persist(next);
      const scene = next.scenes[activeSceneId];
      const toIndex = scene?.cueIds.indexOf(cueId) ?? 0;
      sendSync({
        type: "cue.move",
        projectId: doc.production.id,
        sceneId: activeSceneId,
        cueId,
        toIndex,
      });
    },
    [activeSceneId, doc, licenseStatus, persist, sendSync],
  );

  const onMoveScene = useCallback(
    (
      sceneId: Id,
      beforeSceneId: Id | null,
      options?: { renumber?: boolean },
    ) => {
      if (licenseStatus === "readonly" || !activeEpisodeId) return;
      void persist((prev) =>
        moveScene(prev, activeEpisodeId, sceneId, beforeSceneId, options),
      );
    },
    [activeEpisodeId, licenseStatus, persist],
  );

  const onReorderEpisodes = useCallback(
    (orderedIds: Id[]) => {
      if (licenseStatus === "readonly") return;
      void persist((prev) => reorderEpisodes(prev, orderedIds));
    },
    [licenseStatus, persist],
  );

  const onMoveEpisodeToSeason = useCallback(
    (episodeId: Id, destSeasonId: Id) => {
      if (licenseStatus === "readonly") return;
      void persist((prev) => moveEpisodeToSeason(prev, episodeId, destSeasonId));
    },
    [licenseStatus, persist],
  );

  const onChangeSceneNumberingMode = useCallback(
    (mode: "sequential" | "custom") => {
      if (licenseStatus === "readonly" || !activeEpisodeId) return;
      void persist((prev) =>
        updateEpisodeMeta(prev, activeEpisodeId, {
          sceneNumberingMode: mode,
        }),
      );
    },
    [activeEpisodeId, licenseStatus, persist],
  );

  const onChangeEpisodeNumberingMode = useCallback(
    (mode: "sequential" | "custom") => {
      if (licenseStatus === "readonly") return;
      void persist((prev) =>
        updateProductionMeta(prev, { episodeNumberingMode: mode }),
      );
    },
    [licenseStatus, persist],
  );

  const onChangeEpisodeNumber = useCallback(
    (episodeId: Id, number: string) => {
      if (licenseStatus === "readonly") return;
      void persist((prev) => updateEpisodeMeta(prev, episodeId, { number }));
    },
    [licenseStatus, persist],
  );

  const onSetVisualSpan = useCallback(
    (
      cueId: Id,
      span: number,
      coveredVisual?: "clear" | "merge",
    ) => {
      if (!doc || licenseStatus === "readonly") return;
      void persist((prev) =>
        setVisualRowSpan(prev, cueId, span, {
          coveredVisual,
        }),
      );
    },
    [doc, licenseStatus, persist],
  );

  const onSetAudioSpan = useCallback(
    (
      cueId: Id,
      span: number,
      coveredAudio?: "clear" | "merge",
    ) => {
      if (!doc || licenseStatus === "readonly") return;
      void persist((prev) =>
        setAudioRowSpan(prev, cueId, span, {
          coveredAudio,
        }),
      );
    },
    [doc, licenseStatus, persist],
  );

  const onAddScene = useCallback(() => {
    if (!doc || licenseStatus === "readonly" || !activeEpisodeId) return;
    const { doc: next, sceneId } = addScene(doc, activeEpisodeId);
    setActiveSceneId(sceneId);
    void persist(next);
  }, [activeEpisodeId, doc, licenseStatus, persist]);

  const onDeleteScene = useCallback(
    (sceneId: Id) => {
      if (licenseStatus === "readonly" || !activeEpisodeId) return;
      void (async () => {
        const next = await persist((prev) => moveSceneToTrash(prev, sceneId));
        if (!next) return;
        const episode = next.episodes[activeEpisodeId];
        const stillNormal =
          episode && isNormalEpisode(next, episode);
        if (!stillNormal) {
          const fallback = listEpisodes(next)[0] ?? null;
          setActiveEpisodeId(fallback?.id ?? null);
          setActiveSceneId(fallback?.sceneIds[0] ?? null);
          return;
        }
        if (activeSceneId === sceneId) {
          setActiveSceneId(episode.sceneIds[0] ?? null);
        }
      })();
    },
    [activeEpisodeId, activeSceneId, licenseStatus, persist],
  );

  const onArchiveScene = useCallback(
    (
      sceneId: Id,
      options: {
        mode: "new" | "replaceOne" | "replaceAll";
        replaceArchivedSceneId?: Id;
        label?: string;
      },
    ) => {
      if (licenseStatus === "readonly") return;
      void persist((prev) => {
        try {
          return archiveScene(prev, sceneId, options);
        } catch (err) {
          console.error(err);
          window.alert(
            err instanceof Error
              ? err.message
              : "Kunne ikke arkivere scenen.",
          );
          return prev;
        }
      });
    },
    [licenseStatus, persist],
  );

  const onSubmitDirectorScene = useCallback(
    async (
      originEpisodeId: Id,
      liveSceneId: Id,
      payload: DirectorScenePayload,
    ): Promise<Id | null> => {
      if (licenseStatus === "readonly") return null;
      let submittedSceneId: Id | null = null;
      await persist((prev) => {
        try {
          const result = submitDirectorScene(
            prev,
            originEpisodeId,
            liveSceneId,
            payload,
            { userId: user.userId, displayName: user.displayName },
          );
          submittedSceneId = result.submittedSceneId;
          return result.doc;
        } catch (err) {
          console.error(err);
          window.alert(
            err instanceof Error
              ? err.message
              : "Kunne ikke aflevere scenen.",
          );
          return prev;
        }
      });
      if (submittedSceneId) {
        window.alert(
          "Scenen er afleveret og ligger under Afleverede scener på forsiden. Den er låst i dit tilrettelæggermanus, indtil du låser den op.",
        );
      }
      return submittedSceneId;
    },
    [licenseStatus, persist, user.displayName, user.userId],
  );

  const onDirectorDocumentMutate = useCallback(
    (mutate: (doc: ScriptDocument) => ScriptDocument) => {
      if (licenseStatus === "readonly") return;
      void persist((prev) => {
        try {
          return mutate(prev);
        } catch (err) {
          console.error(err);
          return prev;
        }
      });
    },
    [licenseStatus, persist],
  );

  const onInsertSubmittedIntoProgram = useCallback(
    (
      submittedSceneId: Id,
      options: { mode: "replace" | "keep" },
    ) => {
      if (licenseStatus === "readonly") return;
      void persist((prev) => {
        try {
          return insertSubmittedIntoProgram(prev, submittedSceneId, options);
        } catch (err) {
          console.error(err);
          window.alert(
            err instanceof Error
              ? err.message
              : "Kunne ikke indsætte den afleverede scene.",
          );
          return prev;
        }
      });
    },
    [licenseStatus, persist],
  );

  const onDuplicateScene = useCallback(
    (sceneId: Id) => {
      if (!doc || licenseStatus === "readonly") return;
      const { doc: next, sceneId: newId } = duplicateScene(doc, sceneId);
      setActiveSceneId(newId);
      void persist(next);
    },
    [doc, licenseStatus, persist],
  );

  const onCopySceneToEpisode = useCallback(
    (sceneId: Id, destEpisodeId: Id) => {
      if (!doc || licenseStatus === "readonly") return;
      const { doc: next } = copySceneToEpisode(doc, sceneId, destEpisodeId);
      void persist(next);
    },
    [doc, licenseStatus, persist],
  );

  const onMoveSceneToEpisode = useCallback(
    (sceneId: Id, destEpisodeId: Id) => {
      if (!doc || licenseStatus === "readonly") return;
      const next = moveSceneToEpisode(doc, sceneId, destEpisodeId);
      if (next === doc) return;
      setActiveEpisodeId(destEpisodeId);
      setActiveSceneId(sceneId);
      void persist(next);
    },
    [doc, licenseStatus, persist],
  );

  const onLoadProductionEpisodes = useCallback(async (productionId: Id) => {
    const s = storeRef.current;
    if (!s) return [];
    const dest = await s.loadProject(productionId);
    if (!dest) return [];
    const normalized = normalizeDocument(dest);
    return listEpisodes(normalized).map((ep) => ({
      id: ep.id,
      label: formatEpisodeDisplayTitle(ep, true),
    }));
  }, []);

  const onCopySceneToProduction = useCallback(
    (sceneId: Id, destProductionId: Id, destEpisodeId: Id) => {
      if (licenseStatus === "readonly") return;
      const source = docRef.current;
      const s = storeRef.current;
      if (!source || !s) return;
      void (async () => {
        try {
          const dest = await s.loadProject(destProductionId);
          if (!dest) {
            window.alert("Kunne ikke indlæse destinationen.");
            return;
          }
          const { doc: nextDest } = copySceneToDocument(
            source,
            sceneId,
            normalizeDocument(dest),
            destEpisodeId,
          );
          await s.saveProject(nextDest);
          await refreshProjects();
          window.alert("Scene kopieret til den valgte produktion.");
        } catch (err) {
          console.error(err);
          window.alert("Kopiering mislykkedes.");
        }
      })();
    },
    [licenseStatus, refreshProjects],
  );

  const onMoveSceneToProduction = useCallback(
    (sceneId: Id, destProductionId: Id, destEpisodeId: Id) => {
      if (licenseStatus === "readonly") return;
      const source = docRef.current;
      const s = storeRef.current;
      if (!source || !s) return;
      const scene = source.scenes[sceneId];
      const episodeId = scene?.episodeId;
      if (!scene || !episodeId) return;
      const name = scene.title.trim() || "Scenen";
      if (
        !window.confirm(
          `Flyt «${name}» til den valgte produktion? Den fjernes fra denne produktion.`,
        )
      ) {
        return;
      }
      void (async () => {
        try {
          const dest = await s.loadProject(destProductionId);
          if (!dest) {
            window.alert("Kunne ikke indlæse destinationen.");
            return;
          }
          const { doc: nextDest } = copySceneToDocument(
            source,
            sceneId,
            normalizeDocument(dest),
            destEpisodeId,
          );
          await s.saveProject(nextDest);
          const nextSource = await persist((prev) =>
            removeScene(prev, episodeId, sceneId, { renumber: true }),
          );
          if (nextSource && activeSceneId === sceneId) {
            const ep = nextSource.episodes[episodeId];
            setActiveSceneId(ep?.sceneIds[0] ?? null);
            setFocusCell(null);
          }
          await refreshProjects();
        } catch (err) {
          console.error(err);
          window.alert("Flytning mislykkedes.");
        }
      })();
    },
    [activeSceneId, licenseStatus, persist, refreshProjects],
  );

  const onSplitScene = useCallback(
    (sceneId: Id, atCueId: Id) => {
      if (!doc || licenseStatus === "readonly") return;
      const { doc: next, sceneId: newId } = splitScene(doc, sceneId, atCueId);
      if (next === doc) return;
      setActiveSceneId(newId);
      void persist(next);
    },
    [doc, licenseStatus, persist],
  );

  const onMergeScenes = useCallback(
    (
      keepSceneId: Id,
      otherSceneId: Id,
      identity: { title: string; number?: ItemNumber },
    ) => {
      if (!doc || licenseStatus === "readonly") return;
      const next = mergeScenes(doc, keepSceneId, otherSceneId, identity);
      if (next === doc) return;
      setActiveSceneId(keepSceneId);
      void persist(next);
    },
    [doc, licenseStatus, persist],
  );

  const onAddDialogue = useCallback(
    (sceneId: Id) => {
      // Replikfunktion: UI gated by SHOW_REPLIK_UI in
      // packages/editor/src/replikFeature.ts (knappen «Tilføj replik»).
      if (!doc || licenseStatus === "readonly") return;
      const cast = [...(doc.production.castMembers ?? [])].sort(
        (a, b) => a.sortOrder - b.sortOrder,
      );
      let name: string | null = null;
      if (cast.length > 0) {
        const list = cast.map((m, i) => `${i + 1}. ${m.name}`).join("\n");
        const answer = window.prompt(
          `Vælg medvirkende (nummer) eller skriv navn:\n${list}`,
          cast[0]!.name,
        );
        if (!answer) return;
        const trimmed = answer.trim();
        const asNum = Number(trimmed);
        if (
          Number.isInteger(asNum) &&
          asNum >= 1 &&
          asNum <= cast.length
        ) {
          name = cast[asNum - 1]!.name;
        } else {
          name = trimmed;
        }
      } else {
        name = window.prompt("Karakternavn", "ANNA");
        if (!name?.trim()) return;
        name = name.trim();
      }
      void persist(addDialogueCue(doc, sceneId, name).doc);
    },
    [doc, licenseStatus, persist],
  );

  const onAddCastMember = useCallback(
    (name: string) => {
      if (!doc || licenseStatus === "readonly") return;
      void persist(addCastMember(doc, name).doc);
    },
    [doc, licenseStatus, persist],
  );

  const onUpdateCastMember = useCallback(
    (
      memberId: Id,
      patch: Partial<
        Pick<CastMember, "name" | "color" | "showNameAboveLines">
      >,
    ) => {
      if (!doc || licenseStatus === "readonly") return;
      void persist(updateCastMember(doc, memberId, patch));
    },
    [doc, licenseStatus, persist],
  );

  const onRemoveCastMember = useCallback(
    (memberId: Id) => {
      if (!doc || licenseStatus === "readonly") return;
      void persist(removeCastMember(doc, memberId));
    },
    [doc, licenseStatus, persist],
  );

  const onAddComment = useCallback(
    (
      cueId: Id,
      column: ScriptColumn,
      body: string,
      range?: { from: number; to: number; quote: string },
    ) => {
      if (!doc || licenseStatus === "readonly") return;
      void persist(
        addComment(doc, {
          cueId,
          column,
          body,
          authorId: user.userId,
          authorName: user.displayName,
          from: range?.from,
          to: range?.to,
          quote: range?.quote,
        }).doc,
      );
    },
    [doc, licenseStatus, persist, user.displayName, user.userId],
  );

  const onResolveComment = useCallback(
    (commentId: Id) => {
      if (!doc || licenseStatus === "readonly") return;
      void persist(updateComment(doc, commentId, { resolved: true }));
    },
    [doc, licenseStatus, persist],
  );

  const onReopenComment = useCallback(
    (commentId: Id) => {
      if (!doc || licenseStatus === "readonly") return;
      void persist(updateComment(doc, commentId, { resolved: false }));
    },
    [doc, licenseStatus, persist],
  );

  const onRemoveComment = useCallback(
    (commentId: Id) => {
      if (!doc || licenseStatus === "readonly") return;
      void persist(removeComment(doc, commentId));
    },
    [doc, licenseStatus, persist],
  );

  const onSetCharacter = useCallback(
    (cueId: Id, column: ScriptColumn, characterName: string | null) => {
      if (licenseStatus === "readonly") return;
      void persist((prev) =>
        setCueCharacter(prev, cueId, column, characterName, user.userId),
      );
    },
    [licenseStatus, persist, user.userId],
  );

  const onNewPair = useCallback(
    (
      afterCueId: Id,
      column: ScriptColumn,
      flushedContent: string,
      place: "after" | "before" = "after",
    ) => {
      if (licenseStatus === "readonly" || !activeSceneId) return null;
      let newCueId: Id | null = null;
      void persist((prev) => {
        const cue = prev.cues[afterCueId];
        if (!cue) return prev;
        const baseRevision = getCueSide(cue, column).revision;
        const flushed = updateCueSide(
          prev,
          afterCueId,
          column,
          flushedContent,
          user.userId,
        );
        sendSync({
          type: "cue.upsert",
          projectId: flushed.production.id,
          cue: flushed.cues[afterCueId],
          column,
          baseRevision,
        });
        const inserted =
          place === "before"
            ? insertCueBefore(flushed, activeSceneId, afterCueId)
            : insertCueAfter(flushed, activeSceneId, afterCueId);
        newCueId = inserted.cueId;
        return inserted.doc;
      });
      if (newCueId) setFocusCell({ cueId: newCueId, column });
      return newCueId;
    },
    [activeSceneId, licenseStatus, persist, sendSync, user.userId],
  );

  const onDeletePair = useCallback(
    (cueId: Id) => {
      if (licenseStatus === "readonly" || !activeSceneId) return;
      // Cancel a pending cue debounce flush — it may still hold pre-delete text
      // and would race the full-document save.
      window.clearTimeout(cueSaveTimerRef.current);
      void persist((prev) => removeCue(prev, activeSceneId, cueId));
    },
    [activeSceneId, licenseStatus, persist],
  );

  const onDeletePairs = useCallback(
    (cueIds: Id[]) => {
      if (licenseStatus === "readonly" || !activeSceneId || cueIds.length === 0)
        return;
      window.clearTimeout(cueSaveTimerRef.current);
      void persist((prev) => {
        let doc = prev;
        const scene = doc.scenes[activeSceneId];
        if (!scene) return prev;
        const ordered = [...scene.cueIds]
          .filter((id) => cueIds.includes(id))
          .reverse();
        for (const id of ordered) {
          doc = removeCue(doc, activeSceneId, id);
        }
        return doc;
      });
    },
    [activeSceneId, licenseStatus, persist],
  );

  const onRenameProduction = useCallback(
    (title: string) => {
      if (licenseStatus === "readonly") return;
      void persist((prev) => updateProductionMeta(prev, { title }));
    },
    [licenseStatus, persist],
  );

  const onRenameEpisode = useCallback(
    (episodeId: Id, title: string) => {
      if (licenseStatus === "readonly") return;
      void persist((prev) => updateEpisodeMeta(prev, episodeId, { title }));
    },
    [licenseStatus, persist],
  );

  const onSelectEpisode = useCallback((episodeId: Id) => {
    setActiveEpisodeId(episodeId);
    const ep = docRef.current?.episodes[episodeId];
    setActiveSceneId(ep?.sceneIds[0] ?? null);
    setFocusCell(null);
  }, []);

  const onShareLocation = useCallback((loc: ShareLocation) => {
    writeShareUrl(loc);
  }, []);

  const onSetUseSeasons = useCallback(
    (enabled: boolean) => {
      if (licenseStatus === "readonly") return;
      void persist((prev) => setProductionUseSeasons(prev, enabled));
    },
    [licenseStatus, persist],
  );

  const onAddSeason = useCallback(() => {
    if (licenseStatus === "readonly") return;
    void (async () => {
      let episodeId: Id | null = null;
      const next = await persist((prev) => {
        const base = prev.production.useSeasons
          ? prev
          : setProductionUseSeasons(prev, true);
        const { doc: withSeason, seasonId } = addSeason(base);
        const added = addEpisode(withSeason, { seasonId });
        episodeId = added.episodeId;
        return added.doc;
      });
      if (!next || !episodeId) return;
      setActiveEpisodeId(episodeId);
      setActiveSceneId(next.episodes[episodeId]?.sceneIds[0] ?? null);
      setFocusCell(null);
    })();
  }, [licenseStatus, persist]);

  const onAddEpisode = useCallback(
    (seasonId?: Id | null) => {
      if (licenseStatus === "readonly") return;
      void (async () => {
        let episodeId: Id | null = null;
        const next = await persist((prev) => {
          const resolvedSeasonId =
            seasonId ??
            (prev.production.useSeasons
              ? (activeEpisodeId &&
                  prev.episodes[activeEpisodeId]?.seasonId) ||
                prev.production.seasonIds[0] ||
                null
              : null);
          const added = addEpisode(prev, { seasonId: resolvedSeasonId });
          episodeId = added.episodeId;
          return added.doc;
        });
        if (!next || !episodeId) return;
        setActiveEpisodeId(episodeId);
        setActiveSceneId(next.episodes[episodeId]?.sceneIds[0] ?? null);
        setFocusCell(null);
      })();
    },
    [activeEpisodeId, licenseStatus, persist],
  );

  const onDeleteEpisode = useCallback(
    (episodeId: Id) => {
      if (licenseStatus === "readonly") return;
      void (async () => {
        const next = await persist((prev) => {
          const result = moveEpisodeToTrash(prev, episodeId);
          if (result === prev && listEpisodes(prev).length <= 1) {
            window.alert(
              "Den sidste episode kan ikke flyttes til papirkurven.",
            );
          }
          return result;
        });
        if (!next) return;
        if (activeEpisodeId !== episodeId) return;
        const fallback = listEpisodes(next)[0] ?? null;
        setActiveEpisodeId(fallback?.id ?? null);
        setActiveSceneId(fallback?.sceneIds[0] ?? null);
        setFocusCell(null);
      })();
    },
    [activeEpisodeId, licenseStatus, persist],
  );

  const onRestoreTrashEpisode = useCallback(
    (episodeId: Id) => {
      if (licenseStatus === "readonly") return;
      void persist((prev) => {
        try {
          const ep = prev.episodes[episodeId];
          if (!ep) return prev;
          const originId = ep.originEpisodeId;
          const origin =
            originId && prev.episodes[originId]
              ? prev.episodes[originId]!
              : null;
          if (
            origin &&
            origin.id !== episodeId &&
            isNormalEpisode(prev, origin)
          ) {
            let next = prev;
            for (const sceneId of [...ep.sceneIds]) {
              next = restoreSceneFromTrash(next, sceneId);
            }
            return next;
          }
          return restoreEpisodeFromTrash(prev, episodeId);
        } catch (err) {
          console.error(err);
          window.alert(
            err instanceof Error
              ? err.message
              : "Kunne ikke gendanne fra papirkurven.",
          );
          return prev;
        }
      });
    },
    [licenseStatus, persist],
  );

  const onPurgeTrashEpisode = useCallback(
    (episodeId: Id) => {
      if (licenseStatus === "readonly") return;
      void persist((prev) => permanentlyDeleteEpisode(prev, episodeId));
    },
    [licenseStatus, persist],
  );

  const onRestoreTrashScene = useCallback(
    (sceneId: Id) => {
      if (licenseStatus === "readonly") return;
      void persist((prev) => {
        try {
          return restoreSceneFromTrash(prev, sceneId);
        } catch (err) {
          console.error(err);
          window.alert(
            err instanceof Error
              ? err.message
              : "Kunne ikke gendanne scenen.",
          );
          return prev;
        }
      });
    },
    [licenseStatus, persist],
  );

  const onPurgeTrashScene = useCallback(
    (sceneId: Id) => {
      if (licenseStatus === "readonly") return;
      void persist((prev) => permanentlyDeleteScene(prev, sceneId));
    },
    [licenseStatus, persist],
  );

  const onRestoreArchiveScene = useCallback(
    (
      archivedSceneId: Id,
      options: { mode: "replace" | "keep" },
    ) => {
      if (licenseStatus === "readonly") return;
      void persist((prev) => {
        try {
          return restoreSceneFromArchive(prev, archivedSceneId, options);
        } catch (err) {
          console.error(err);
          window.alert(
            err instanceof Error
              ? err.message
              : "Kunne ikke gendanne den arkiverede version.",
          );
          return prev;
        }
      });
    },
    [licenseStatus, persist],
  );

  const onPurgeArchiveScene = useCallback(
    (archivedSceneId: Id) => {
      if (licenseStatus === "readonly") return;
      void persist((prev) => permanentlyDeleteScene(prev, archivedSceneId));
    },
    [licenseStatus, persist],
  );

  const onPurgeArchiveEpisode = useCallback(
    (episodeId: Id) => {
      if (licenseStatus === "readonly") return;
      void persist((prev) => permanentlyDeleteEpisode(prev, episodeId));
    },
    [licenseStatus, persist],
  );

  const onEmptyTrash = useCallback(() => {
    if (licenseStatus === "readonly") return;
    void persist((prev) => emptyTrash(prev));
  }, [licenseStatus, persist]);

  const onEmptyArchive = useCallback(() => {
    if (licenseStatus === "readonly") return;
    void persist((prev) => emptyArchive(prev));
  }, [licenseStatus, persist]);

  const onDuplicateEpisode = useCallback(
    (episodeId: Id) => {
      if (licenseStatus === "readonly") return;
      void persist((prev) => duplicateEpisode(prev, episodeId).doc);
    },
    [licenseStatus, persist],
  );

  const onExportEpisodeBackup = useCallback((episodeId: Id) => {
    const current = docRef.current;
    if (!current) return;
    try {
      void downloadEpisodeBackup(current, episodeId);
    } catch (err) {
      console.error(err);
      window.alert(
        err instanceof Error
          ? err.message
          : "Kunne ikke gemme sikkerhedskopien.",
      );
    }
  }, []);

  const onImportEpisodeBackup = useCallback(() => {
    if (licenseStatus === "readonly") return;
    void (async () => {
      try {
        const file = await pickEpisodeBackupFile();
        if (!file) return;
        const backup = await readEpisodeBackupFile(file);
        const label =
          backup.episode.title.trim() ||
          formatEpisodeDisplayTitle(backup.episode, true);
        if (
          !window.confirm(
            `Indsæt episoden «${label}» som ny episode i denne produktion?\n\nManus, noter, credits og kommentarer følger med.`,
          )
        ) {
          return;
        }
        const source = docRef.current;
        if (!source) return;
        const { doc: next } = importEpisodeBackup(source, backup);
        void persist(next);
      } catch (err) {
        console.error(err);
        window.alert(
          err instanceof Error
            ? err.message
            : "Kunne ikke indsætte sikkerhedskopien.",
        );
      }
    })();
  }, [licenseStatus, persist]);

  const onCopyEpisodeToProduction = useCallback(
    (episodeId: Id, destProductionId: Id) => {
      if (licenseStatus === "readonly") return;
      const source = docRef.current;
      const s = storeRef.current;
      if (!source || !s) return;
      void (async () => {
        try {
          const dest = await s.loadProject(destProductionId);
          if (!dest) {
            window.alert("Kunne ikke indlæse destinationen.");
            return;
          }
          const { doc: nextDest } = copyEpisodeToDocument(
            source,
            episodeId,
            normalizeDocument(dest),
          );
          await s.saveProject(nextDest);
          await refreshProjects();
          window.alert("Episode kopieret til den valgte produktion.");
        } catch (err) {
          console.error(err);
          window.alert("Kopiering mislykkedes.");
        }
      })();
    },
    [licenseStatus, refreshProjects],
  );

  const onMoveEpisodeToProduction = useCallback(
    (episodeId: Id, destProductionId: Id) => {
      if (licenseStatus === "readonly") return;
      const source = docRef.current;
      const s = storeRef.current;
      if (!source || !s) return;
      const ep = source.episodes[episodeId];
      const title = ep?.title?.trim() || "Episoden";
      if (
        !window.confirm(
          `Flyt «${title}» til den valgte produktion? Den fjernes fra denne produktion.`,
        )
      ) {
        return;
      }
      void (async () => {
        try {
          const dest = await s.loadProject(destProductionId);
          if (!dest) {
            window.alert("Kunne ikke indlæse destinationen.");
            return;
          }
          const { doc: nextDest } = copyEpisodeToDocument(
            source,
            episodeId,
            normalizeDocument(dest),
          );
          await s.saveProject(nextDest);
          const nextSource = await persist((prev) =>
            removeEpisode(prev, episodeId),
          );
          if (nextSource && activeEpisodeId === episodeId) {
            const fallback = listEpisodes(nextSource)[0] ?? null;
            setActiveEpisodeId(fallback?.id ?? null);
            setActiveSceneId(fallback?.sceneIds[0] ?? null);
            setFocusCell(null);
          }
          await refreshProjects();
        } catch (err) {
          console.error(err);
          window.alert("Flytning mislykkedes.");
        }
      })();
    },
    [
      activeEpisodeId,
      licenseStatus,
      persist,
      refreshProjects,
    ],
  );

  const onRenameSeason = useCallback(
    (seasonId: Id, title: string) => {
      if (licenseStatus === "readonly") return;
      void persist((prev) => updateSeasonMeta(prev, seasonId, { title }));
    },
    [licenseStatus, persist],
  );

  const onTogglePrintBorderBelow = useCallback(
    (cueId: Id) => {
      if (licenseStatus === "readonly") return;
      void persist((prev) => {
        const cue = prev.cues[cueId];
        if (!cue) return prev;
        return setCuePrintBorderBelow(
          prev,
          cueId,
          !Boolean(cue.printBorderBelow),
        );
      });
    },
    [licenseStatus, persist],
  );

  const onChangeCrewRoles = useCallback(
    (sceneId: Id, roles: SceneCrewRole[]) => {
      if (licenseStatus === "readonly") return;
      void persist((prev) => setSceneCrewRoles(prev, sceneId, roles), {
        coalesceKey: `crew:${sceneId}`,
      });
      window.clearTimeout(crewSaveTimerRef.current);
      crewSaveTimerRef.current = window.setTimeout(() => {
        void flushPendingSave();
      }, 400);
    },
    [licenseStatus, persist, flushPendingSave],
  );

  const onChangeEpisodeCrewRoles = useCallback(
    (roles: SceneCrewRole[]) => {
      if (licenseStatus === "readonly" || !activeEpisodeId) return;
      const episodeId = activeEpisodeId;
      void persist((prev) => setEpisodeCrewRoles(prev, episodeId, roles), {
        coalesceKey: `crew-ep:${episodeId}`,
      });
      window.clearTimeout(crewSaveTimerRef.current);
      crewSaveTimerRef.current = window.setTimeout(() => {
        void flushPendingSave();
      }, 400);
    },
    [activeEpisodeId, licenseStatus, persist, flushPendingSave],
  );

  const onRememberJobTitle = useCallback(
    (title: string) => {
      if (licenseStatus === "readonly") return;
      void persist((prev) => addCrewJobTitle(prev, title));
    },
    [licenseStatus, persist],
  );

  const onRemoveJobTitle = useCallback(
    (title: string) => {
      if (licenseStatus === "readonly") return;
      void persist((prev) => removeCrewJobTitle(prev, title));
    },
    [licenseStatus, persist],
  );

  const onToggleSeasonCrewPerson = useCallback(
    (
      episodeId: Id,
      jobTitle: string,
      person: { name: string; detail?: string },
      onSeason: boolean,
    ) => {
      if (licenseStatus === "readonly") return;
      void persist((prev) =>
        setCrewPersonOnEpisodePeers(
          prev,
          episodeId,
          jobTitle,
          person,
          onSeason,
        ),
      );
    },
    [licenseStatus, persist],
  );

  const onDeactivateSeasonCrewPerson = useCallback(
    (
      episodeId: Id,
      jobTitle: string,
      person: { name: string; detail?: string },
      mode: CrewSeasonDeactivateMode,
    ) => {
      if (licenseStatus === "readonly") return;
      void persist((prev) =>
        deactivateCrewPersonSeason(prev, episodeId, jobTitle, person, mode),
      );
    },
    [licenseStatus, persist],
  );

  const onRemoveSeasonCrewPerson = useCallback(
    (
      episodeId: Id,
      jobTitle: string,
      person: { name: string; detail?: string },
    ) => {
      if (licenseStatus === "readonly") return;
      void persist((prev) =>
        removeCrewPersonFromEpisodePeers(prev, episodeId, jobTitle, person),
      );
    },
    [licenseStatus, persist],
  );

  const onChangeProgramTexts = useCallback(
    (texts: ProgramText[]) => {
      if (licenseStatus === "readonly" || !activeEpisodeId) return;
      const episodeId = activeEpisodeId;
      void persist((prev) => setEpisodeProgramTexts(prev, episodeId, texts), {
        coalesceKey: `programtext:${episodeId}`,
      });
      window.clearTimeout(programTextSaveTimerRef.current);
      programTextSaveTimerRef.current = window.setTimeout(() => {
        void flushPendingSave();
      }, 400);
    },
    [activeEpisodeId, licenseStatus, persist, flushPendingSave],
  );

  const onCueNoteChange = useCallback(
    (cueId: Id, columnId: Id, text: string) => {
      if (licenseStatus === "readonly") return;
      void persist((prev) => setCueNote(prev, cueId, columnId, text), {
        coalesceKey: `note:${cueId}:${columnId}`,
      });
      dirtyRef.current = true;
      window.clearTimeout(noteSaveTimerRef.current);
      noteSaveTimerRef.current = window.setTimeout(() => {
        void flushPendingSave();
      }, 400);
    },
    [flushPendingSave, licenseStatus, persist],
  );

  const onSceneNoteChange = useCallback(
    (sceneId: Id, columnId: Id, text: string) => {
      if (licenseStatus === "readonly") return;
      void persist((prev) => setSceneNote(prev, sceneId, columnId, text), {
        coalesceKey: `scene-note:${sceneId}:${columnId}`,
      });
      dirtyRef.current = true;
      window.clearTimeout(noteSaveTimerRef.current);
      noteSaveTimerRef.current = window.setTimeout(() => {
        void flushPendingSave();
      }, 400);
    },
    [flushPendingSave, licenseStatus, persist],
  );

  const onChangeNoteColumns = useCallback(
    (columns: Parameters<typeof setProductionNoteColumns>[1]) => {
      if (licenseStatus === "readonly") return;
      void persist((prev) => setProductionNoteColumns(prev, columns));
    },
    [licenseStatus, persist],
  );

  const onRemoveNoteColumn = useCallback(
    (columnId: Id, scope: "episode" | "production") => {
      if (licenseStatus === "readonly") return;
      if (scope === "episode") {
        if (!activeEpisodeId) return;
        void persist((prev) =>
          hideNoteColumnInEpisode(prev, activeEpisodeId, columnId),
        );
        return;
      }
      void persist((prev) => removeNoteColumnEverywhere(prev, columnId));
    },
    [activeEpisodeId, licenseStatus, persist],
  );

  const onCameraMetaChange = useCallback(
    (
      cueId: Id,
      patch: {
        cameraNumber?: number | null;
        cameraNote?: string;
        positionNote?: string;
      },
    ) => {
      if (licenseStatus === "readonly") return;
      void persist((prev) => setCueCameraMeta(prev, cueId, patch));
    },
    [licenseStatus, persist],
  );

  const onToggleStatusStep = useCallback(
    (sceneId: Id, stepId: Id) => {
      if (licenseStatus === "readonly") return;
      void persist((prev) => toggleSceneStatusStep(prev, sceneId, stepId));
    },
    [licenseStatus, persist],
  );

  const onToggleEpisodeStatusStep = useCallback(
    (stepId: Id) => {
      if (licenseStatus === "readonly" || !activeEpisodeId) return;
      void persist((prev) =>
        toggleEpisodeStatusStep(prev, activeEpisodeId, stepId),
      );
    },
    [activeEpisodeId, licenseStatus, persist],
  );

  const onChangeStatusSteps = useCallback(
    (steps: StatusStepDef[]) => {
      if (licenseStatus === "readonly") return;
      void persist((prev) =>
        setProductionStatusSteps(prev, steps),
      );
    },
    [licenseStatus, persist],
  );

  const onChangeSceneStatusSteps = useCallback(
    (sceneId: Id, steps: StatusStepDef[] | null) => {
      if (licenseStatus === "readonly") return;
      void persist((prev) => setSceneStatusSteps(prev, sceneId, steps));
    },
    [licenseStatus, persist],
  );

  const onChangeEpisodeStatusSteps = useCallback(
    (steps: StatusStepDef[]) => {
      if (licenseStatus === "readonly" || !activeEpisodeId) return;
      void persist((prev) =>
        setEpisodeStatusSteps(prev, activeEpisodeId, steps),
      );
    },
    [activeEpisodeId, licenseStatus, persist],
  );

  const onSceneMetaChange = useCallback(
    (
      sceneId: Id,
      patch: Partial<{
        title: string;
        description: string;
        durationSeconds: number;
        number: ItemNumber;
        headerBackground: string;
        heading: string;
        headingKind: "int" | "ext" | undefined;
        headingLocation: string;
        headingTime: string;
        headingNote: string;
      }>,
    ) => {
      if (!doc || licenseStatus === "readonly") return;
      void persist((prev) => updateSceneMeta(prev, sceneId, patch), {
        coalesceKey: `scene-meta:${sceneId}`,
      });
      dirtyRef.current = true;
      window.clearTimeout(sceneMetaSaveTimerRef.current);
      sceneMetaSaveTimerRef.current = window.setTimeout(() => {
        void flushPendingSave();
      }, 400);
    },
    [doc, flushPendingSave, licenseStatus, persist],
  );

  async function activateLicense() {
    const key = licenseKey.trim();
    if (!key) return;
    if (needsName && !claimName.trim()) return;
    setHomeBusy(true);
    try {
      const result = await openApiSession(API_BASE, {
        licenseKey: key,
        displayName: needsName ? claimName.trim() : undefined,
      });
      setLicenseStatus(result.status);
      setLicenseMessage(result.message);
      if (result.needsName) {
        setNeedsName(true);
        setProjects([]);
        return;
      }
      setNeedsName(false);
      setClaimName("");
      if (isUnlockedLicense(result.status)) {
        localStorage.setItem(LICENSE_KEY_STORAGE, key);
        if (result.token) cacheLicense(result.token, key);
        if (result.user) {
          setUser(result.user);
          saveUser(result.user);
        }
        await refreshProjects();
      } else {
        localStorage.removeItem(LICENSE_KEY_STORAGE);
        localStorage.removeItem(LEGACY_LICENSE_KEY_STORAGE);
        setProjects([]);
      }
    } finally {
      setHomeBusy(false);
    }
  }

  if (!store || !licenseReady) {
    return <div className="sm-home-loading">Indlæser Minproduktion.dk…</div>;
  }

  if (!doc) {
    return (
      <ProductionHome
        projects={projects}
        userId={user.userId}
        busy={homeBusy}
        unlocked={isUnlockedLicense(licenseStatus)}
        onOpen={(id) => void handleOpenProduction(id)}
        onCreate={(title) => void handleCreateProduction(title)}
        onDelete={(id) => void handleDeleteProduction(id)}
        licenseKey={licenseKey}
        licenseStatus={licenseStatus}
        licenseMessage={licenseMessage}
        needsName={needsName}
        claimName={claimName}
        onLicenseKeyChange={(key) => {
          setLicenseKey(key);
          if (needsName) setNeedsName(false);
        }}
        onClaimNameChange={setClaimName}
        onCheckLicense={() => void activateLicense()}
        onCancelName={() => {
          setNeedsName(false);
          setClaimName("");
          setLicenseMessage(null);
        }}
        shareError={shareError}
      />
    );
  }

  return (
    <ScriptEditor
      document={doc}
      userId={user.userId}
      displayName={user.displayName}
      activeEpisodeId={activeEpisodeId}
      activeSceneId={activeSceneId}
      view={view}
      presence={presence}
      syncStatus={syncStatus}
      cloudBacked
      licenseStatus={licenseStatus}
      apiBase={API_BASE}
      onSubmitDirectorScene={onSubmitDirectorScene}
      onDirectorDocumentMutate={onDirectorDocumentMutate}
      onInsertSubmittedIntoProgram={onInsertSubmittedIntoProgram}
      onSelectScene={setActiveSceneId}
      onSelectEpisode={onSelectEpisode}
      onChangeView={setView}
      onAddScene={onAddScene}
      onDeleteScene={onDeleteScene}
      onArchiveScene={onArchiveScene}
      onDuplicateScene={onDuplicateScene}
      onCopySceneToEpisode={onCopySceneToEpisode}
      onMoveSceneToEpisode={onMoveSceneToEpisode}
      onCopySceneToProduction={onCopySceneToProduction}
      onMoveSceneToProduction={onMoveSceneToProduction}
      onLoadProductionEpisodes={onLoadProductionEpisodes}
      onSplitScene={onSplitScene}
      onMergeScenes={onMergeScenes}
      onAddSeason={onAddSeason}
      onAddEpisode={onAddEpisode}
      onDeleteEpisode={onDeleteEpisode}
      onRestoreTrashEpisode={onRestoreTrashEpisode}
      onPurgeTrashEpisode={onPurgeTrashEpisode}
      onRestoreTrashScene={onRestoreTrashScene}
      onPurgeTrashScene={onPurgeTrashScene}
      onEmptyTrash={onEmptyTrash}
      onRestoreArchiveScene={onRestoreArchiveScene}
      onPurgeArchiveScene={onPurgeArchiveScene}
      onPurgeArchiveEpisode={onPurgeArchiveEpisode}
      onEmptyArchive={onEmptyArchive}
      onDuplicateEpisode={onDuplicateEpisode}
      onExportEpisodeBackup={onExportEpisodeBackup}
      onImportEpisodeBackup={
        licenseStatus === "readonly" ? undefined : onImportEpisodeBackup
      }
      onCopyEpisodeToProduction={onCopyEpisodeToProduction}
      onMoveEpisodeToProduction={onMoveEpisodeToProduction}
      onMoveEpisodeToSeason={onMoveEpisodeToSeason}
      otherProductions={projects
        .filter((p) => p.id !== doc.production.id)
        .map((p) => ({ id: p.id, title: p.title }))}
      onReorderEpisodes={onReorderEpisodes}
      onSetUseSeasons={onSetUseSeasons}
      onRenameSeason={onRenameSeason}
      onMoveScene={onMoveScene}
      onChangeSceneNumberingMode={onChangeSceneNumberingMode}
      onChangeEpisodeNumberingMode={onChangeEpisodeNumberingMode}
      onChangeEpisodeNumber={onChangeEpisodeNumber}
      onAddDialogue={onAddDialogue}
      onCueFocus={onCueFocus}
      onCueBlur={onCueBlur}
      onCueChange={onCueChange}
      onMoveCueBlock={onMoveCueBlock}
      onNewPair={onNewPair}
      onDeletePair={onDeletePair}
      onDeletePairs={onDeletePairs}
      onSetVisualSpan={onSetVisualSpan}
      onSetAudioSpan={onSetAudioSpan}
      onTogglePrintBorderBelow={onTogglePrintBorderBelow}
      onRenameProduction={onRenameProduction}
      onRenameEpisode={onRenameEpisode}
      onRenameDisplayName={onRenameDisplayName}
      onSceneMetaChange={onSceneMetaChange}
      onAddCastMember={onAddCastMember}
      onUpdateCastMember={onUpdateCastMember}
      onRemoveCastMember={onRemoveCastMember}
      onAddComment={onAddComment}
      onResolveComment={onResolveComment}
      onReopenComment={onReopenComment}
      onRemoveComment={onRemoveComment}
        onSetCharacter={onSetCharacter}
        onChangeCrewRoles={onChangeCrewRoles}
        onChangeEpisodeCrewRoles={onChangeEpisodeCrewRoles}
        onRememberJobTitle={onRememberJobTitle}
        onRemoveJobTitle={onRemoveJobTitle}
        onToggleSeasonCrewPerson={onToggleSeasonCrewPerson}
        onDeactivateSeasonCrewPerson={onDeactivateSeasonCrewPerson}
        onRemoveSeasonCrewPerson={onRemoveSeasonCrewPerson}
        onChangeProgramTexts={onChangeProgramTexts}
        onCueNoteChange={onCueNoteChange}
        onSceneNoteChange={onSceneNoteChange}
        onChangeNoteColumns={onChangeNoteColumns}
        onRemoveNoteColumn={onRemoveNoteColumn}
        onCameraMetaChange={onCameraMetaChange}
        onToggleStatusStep={onToggleStatusStep}
        onToggleEpisodeStatusStep={onToggleEpisodeStatusStep}
        onChangeStatusSteps={onChangeStatusSteps}
        onChangeSceneStatusSteps={onChangeSceneStatusSteps}
        onChangeEpisodeStatusSteps={onChangeEpisodeStatusSteps}
        focusCell={focusCell}
      onFocusCellHandled={(cell) => {
        setFocusCell((cur) =>
          cur?.cueId === cell.cueId && cur.column === cell.column ? null : cur,
        );
      }}
      contentResetKey={historyVersion}
      onGoHome={handleGoHome}
      initialScreen={editorInit.screen}
      initialNoteColumnId={editorInit.noteColumnId}
      shareLinkBase={pageUrlBase()}
      onShareLocation={onShareLocation}
    />
  );
}
